import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-meeting-create-dialog',
  templateUrl: './meeting-create-dialog.component.html',
  styleUrls: ['./meeting-create-dialog.component.scss']
})
export class MeetingCreateDialogComponent {
  readonly audioFirstThreshold = 6;
  readonly roomModes = [
    { value: 'Video', title: 'Video Room', blurb: 'Face-to-face meeting with camera and mic.', icon: 'videocam' },
    { value: 'Audio', title: 'Audio Room', blurb: 'Fast voice meeting for standups and quick syncs.', icon: 'headset_mic' }
  ];

  form = this.fb.group({
    title: ['', Validators.required],
    description: [''],
    meetingDate: ['', Validators.required],
    startTime: ['', Validators.required],
    endTime: ['', Validators.required],
    roomType: ['Video', Validators.required],
    roomName: [''],
    meetingLink: [''],
    attendeeStaffIds: [[], Validators.required]
  });

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<MeetingCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { staffOptions: any[] }
  ) {}

  get selectedCount(): number {
    const selected = this.form.get('attendeeStaffIds')?.value as number[] | null;
    return selected?.length || 0;
  }

  get selectedMode(): string {
    return this.form.get('roomType')?.value || 'Video';
  }

  get shouldRecommendAudio(): boolean {
    return this.selectedCount > this.audioFirstThreshold;
  }

  attendeePreview(): any[] {
    const selectedIds = new Set<number>(this.form.get('attendeeStaffIds')?.value || []);
    return (this.data.staffOptions || []).filter((staff) => selectedIds.has(staff.staffId)).slice(0, 6);
  }

  chooseRoomMode(mode: string): void {
    this.form.patchValue({ roomType: mode });
    if (!this.form.get('roomName')?.value?.trim()) {
      this.form.patchValue({ roomName: mode === 'Audio' ? 'Audio Briefing Room' : 'Video Collaboration Room' });
    }
  }

  onAttendeesChanged(): void {
    if (this.shouldRecommendAudio && this.selectedMode === 'Video') {
      this.chooseRoomMode('Audio');
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const startTime = this.combineDateTime(value.meetingDate || '', value.startTime || '');
    const endTime = this.combineDateTime(value.meetingDate || '', value.endTime || '');

    if (!startTime || !endTime || endTime <= startTime) {
      this.form.get('endTime')?.setErrors({ invalidRange: true });
      this.form.get('endTime')?.markAsTouched();
      return;
    }

    this.dialogRef.close({
      title: value.title?.trim(),
      description: value.description?.trim() || null,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      roomType: value.roomType,
      roomName: value.roomName?.trim() || null,
      meetingLink: value.meetingLink?.trim() || null,
      attendeeStaffIds: value.attendeeStaffIds || []
    });
  }

  private combineDateTime(date: string, time: string): Date | null {
    if (!date || !time) return null;
    const value = new Date(`${date}T${time}:00`);
    return Number.isNaN(value.getTime()) ? null : value;
  }
}
